import { facetLabel, type ListFilters } from './game-filters';

/** The longest a suggested name should be (a typed name keeps the field's own cap). */
export const SUGGESTED_NAME_MAX = 32;

/** Separates a type from its subtype in a type key (`TABLE:BLACKJACK`), as game-filters writes it. */
const SUBTYPE_SEP = ':';

/** The labels of each facet's picks, in the order they were picked. */
type Parts = { providers: string[]; themes: string[]; types: string[] };

/** What a recomposition leaves out (rule 6). */
type Drop = { providers?: boolean; themes?: boolean };

/** Rule 6's recompositions, each leaving out one more group than the one before. */
const DROP_STEPS: readonly Drop[] = [{}, { providers: true }, { providers: true, themes: true }];

/*
 * How a name is built (the spec's "Suggested names" rules, in order). Pure, and English on purpose: the
 * words here ("Games", "&", "& more") are part of a title the site shows, so none of them is hub copy.
 *  - `typeLabel(key)`: one type pick. A subtype pick (`TABLE:BLACKJACK`) is the subtype's own label
 *    ("Blackjack"); a whole type is its label plus " Games" unless that already ends in an s ("Slots",
 *    "Table Games").
 *  - `group(labels)`: one facet's picks as text. One is the label, two are `a & b`, three or more `a & more`.
 *    The types' pair says Games once when both its labels end in it (`typeGroup`: "Arcade & Bingo Games").
 *  - `compose(parts, drop)`: a whole name from the three label lists. Providers go when there is more than one
 *    and a theme or type goes with them (rule 3). Then a theme or type group of three or more goes when
 *    another group remains (rule 5; themes are judged before types, so the types are the last to go). Then
 *    whatever `drop` asks for goes, while another group remains (rule 6). What is left reads as providers
 *    alone (rule 4: one ends with Games unless its name's last word already is Games), else as provider, theme,
 *    type (rule 1), and a theme with no type ends with Games unless the theme group reads "& more" or its last
 *    word already is Games (rule 2).
 *  - `suggestListName`: composes with everything, and while that is over the limit composes again with one
 *    more group left out, stopping at the first name that fits. A name still over once nothing more can go
 *    is the last one composed: a word is never cut. Rule 7 comes last, so its number does not count.
 */

const typeLabel = (key: string): string => {
  const sep = key.indexOf(SUBTYPE_SEP);
  if (sep >= 0) return facetLabel(key.slice(sep + 1));
  const label = facetLabel(key);
  return label.toLowerCase().endsWith('s') ? label : `${label} Games`;
};

/** Whether a name's last word already is Games, whatever its case: rules 2 and 4 add none then. */
const endsInGames = (name: string): boolean => {
  const lower = name.toLowerCase();
  return lower === 'games' || lower.endsWith(' games');
};

const group = (labels: readonly string[]): string => {
  if (labels.length < 2) return labels[0] ?? '';
  return labels.length === 2 ? `${labels[0]} & ${labels[1]}` : `${labels[0]} & more`;
};

/** The types as text: as `group`, except that a pair whose labels both end in Games says it once, after the second
 *  ("Arcade & Bingo Games", as rule 5's "Slots & Crash Games"). Types only: a provider's Games is its name's. */
const typeGroup = (labels: readonly string[]): string => {
  if (labels.length === 2 && labels.every(endsInGames)) {
    const first = labels[0].slice(0, -' Games'.length);
    if (first.trim() !== '') return `${first} & ${labels[1]}`;
  }
  return group(labels);
};

const compose = (parts: Parts, drop: Drop): string => {
  let { providers, themes, types } = parts;
  if (providers.length > 1 && themes.length + types.length > 0) providers = [];
  if (themes.length > 2 && providers.length + types.length > 0) themes = [];
  if (types.length > 2 && providers.length + themes.length > 0) types = [];
  if (drop.providers && themes.length + types.length > 0) providers = [];
  if (drop.themes && providers.length + types.length > 0) themes = [];

  if (themes.length + types.length === 0) {
    if (providers.length !== 1) return group(providers);
    return endsInGames(providers[0]) ? providers[0] : `${providers[0]} Games`;
  }
  const name = [group(providers), group(themes), typeGroup(types)].filter((part) => part !== '').join(' ');
  if (types.length > 0) return name;
  return endsInGames(name) || name.endsWith('& more') ? name : `${name} Games`;
};

/** How names on the page are compared: trimmed, ignoring case. */
const nameKey = (name: string): string => name.trim().toLowerCase();

/** The name itself when it is free, else with the first " 2", " 3", ... that is. */
export const firstFree = (name: string, taken: readonly string[]): string => {
  const used = taken.map(nameKey);
  if (!used.includes(nameKey(name))) return name;
  for (let n = 2; ; n += 1) {
    const candidate = `${name} ${n}`;
    if (!used.includes(nameKey(candidate))) return candidate;
  }
};

/**
 * The name an automatic list gets from its filters (the spec's rules, in order). English on purpose: the
 * catalogue's type and theme values are English, and the name is the title the site shows, not hub copy, so
 * "Games", "&" and "& more" never go through the translation function. `providerLabel` is the chip's own label
 * for a provider id (display name, else the chip's fallback). `taken` are the names already on the page (the
 * owner's and the site's, the edited list left out); a clash gets " 2", " 3", ... the first free, compared
 * trimmed and ignoring case. '' when there is no filter.
 */
export const suggestListName = (
  filters: ListFilters,
  providerLabel: (id: string) => string,
  taken: readonly string[],
): string => {
  const parts: Parts = {
    providers: filters.providers.map((id) => providerLabel(id)),
    themes: filters.themes.map((value) => facetLabel(value)),
    types: filters.types.map((key) => typeLabel(key)),
  };
  let name = '';
  for (const drop of DROP_STEPS) {
    name = compose(parts, drop);
    if (name.length <= SUGGESTED_NAME_MAX) break;
  }
  return name === '' ? '' : firstFree(name, taken);
};
